import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc, trpcErrorCode, trpcErrorDetails } from "../../../app/trpc.js";
import { Button, Card, Chip, Input, Notice, Spinner } from "../../../ui/index.js";
import { DEFAULT_HOURS, HoursGrid, HoursSaveState, useHoursAutosave } from "../HoursGrid.js";
import { StepNav } from "../StepNav.js";
import type { StepProps } from "../types.js";
import { useBeforeUnloadGuard, useUnsavedChanges } from "../unsaved.js";

interface Master {
  id: string;
  name: string;
  serviceIds: string[];
}

/** Карточка мастера: услуги сохраняются сразу, карточка сворачивается, мастер удаляется, если записей ещё нет. */
function MasterCard({
  salonId,
  master,
  services,
  justAdded,
}: {
  salonId: string;
  master: Master;
  services: Array<{ id: string; name: string }>;
  justAdded: boolean;
}) {
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const hours = useQuery(trpc.calendar.workingHours.get.queryOptions({ masterId: master.id }));
  const autosave = useHoursAutosave(master.id, hours.data);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [serviceIds, setServiceIds] = useState(master.serviceIds);
  const [servicesFailed, setServicesFailed] = useState(false);
  const latest = useRef(master.serviceIds);
  const saved = useRef(master.serviceIds);
  const chain = useRef(Promise.resolve());
  useEffect(() => {
    if (justAdded) root.current?.scrollIntoView({ block: "nearest" });
  }, [justAdded]);
  // Услуги и график сохраняются сами; несохранённым остаётся только график, который сам не сохранится
  // (ошибка, отказ сервера, неверный интервал): тогда уход с шага спросит подтверждение.
  useUnsavedChanges(autosave.unsaved);
  // Правки ещё в пути: переход внутри кабинета их дождётся (сохранение при размонтировании), а закрытие вкладки нет.
  useBeforeUnloadGuard(autosave.notOnServer);
  const saveMaster = useMutation(trpc.calendar.masters.save.mutationOptions());
  const remove = useMutation(
    trpc.calendar.masters.remove.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.masters.pathFilter());
        // Владелец мог быть привязан к удалённому мастеру, а контекст кабинета кэшируется.
        void queryClient.invalidateQueries(trpc.cabinet.context.pathFilter());
      },
    }),
  );
  const toggleService = (id: string) => {
    const next = latest.current.includes(id) ? latest.current.filter((x) => x !== id) : [...latest.current, id];
    latest.current = next;
    setServiceIds(next);
    setServicesFailed(false);
    const snapshot = next;
    chain.current = chain.current.then(async () => {
      if (snapshot !== latest.current) return;
      try {
        await saveMaster.mutateAsync({ id: master.id, salonId, name: master.name, serviceIds: snapshot });
        saved.current = snapshot;
        await queryClient.invalidateQueries(trpc.calendar.masters.list.queryFilter({ salonId }));
      } catch {
        if (latest.current !== snapshot) return;
        latest.current = saved.current;
        setServiceIds(saved.current);
        setServicesFailed(true);
      }
    });
  };
  const days = (autosave.grid ?? hours.data ?? []).length;
  return (
    <div ref={root}>
      <Card className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="flex min-w-0 items-center gap-1 text-left" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            <svg
              viewBox="0 0 20 20"
              aria-hidden="true"
              className={`h-5 w-5 shrink-0 text-ink-2 transition-transform ${expanded ? "rotate-90" : ""}`}
              fill="currentColor"
            >
              <path d="M7.2 4.5a1 1 0 0 1 1.4 0l5 5.2a1 1 0 0 1 0 1.4l-5 5.2a1 1 0 1 1-1.4-1.4L11.4 10 7.2 5.9a1 1 0 0 1 0-1.4z" />
            </svg>
            <span className="sr-only">{expanded ? t("onb.masters.collapse") : t("onb.masters.expand")}</span>
            <span className="truncate font-medium">{master.name}</span>
          </button>
          <button type="button" className="shrink-0 text-sm text-red-700 underline" onClick={() => setConfirmDelete(true)}>
            {t("onb.masters.delete")}
          </button>
        </div>
        {confirmDelete && (
          <div className="space-y-2">
            <p className="text-sm text-ink-2">{t("onb.masters.deleteAsk", { name: master.name })}</p>
            {remove.isError && (
              <Notice kind="error">
                {trpcErrorCode(remove.error) !== "conflict"
                  ? t("common.error")
                  : trpcErrorDetails(remove.error)?.reason === "linked"
                    ? t("onb.masters.deleteLinked")
                    : t("onb.masters.deleteBlocked")}
              </Notice>
            )}
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setConfirmDelete(false)}>
                {t("onb.masters.cancel")}
              </Button>
              <Button className="flex-1" disabled={remove.isPending} onClick={() => remove.mutate({ salonId, masterId: master.id })}>
                {t("onb.masters.deleteYes")}
              </Button>
            </div>
          </div>
        )}
        {expanded && (
          <>
            <div className="text-right text-sm text-ink-3">{days > 0 ? t("onb.masters.hasHours") : t("onb.masters.noHours")}</div>
            <div className="flex flex-wrap gap-2">
              {services.map((s) => {
                const on = serviceIds.includes(s.id);
                return (
                  <Chip key={s.id} active={on} onClick={() => toggleService(s.id)}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className={on ? "inline-block w-4" : "inline-block w-4 text-transparent"} aria-hidden="true">
                        ✓
                      </span>
                      {s.name}
                    </span>
                  </Chip>
                );
              })}
            </div>
            {servicesFailed && <Notice kind="error">{t("onb.masters.saveFailed")}</Notice>}
            {serviceIds.length === 0 && <Notice kind="error">{t("onb.masters.noServices")}</Notice>}
            <Button variant="secondary" className="w-full" onClick={() => setHoursOpen(!hoursOpen)}>
              {hoursOpen ? t("onb.masters.hideHours") : t("onb.masters.editHours")}
            </Button>
            {hoursOpen &&
              (autosave.grid === null ? (
                <Spinner />
              ) : (
                <>
                  <HoursGrid grid={autosave.grid} onChange={autosave.setGrid} />
                  <HoursSaveState status={autosave.status} onRetry={autosave.retry} />
                </>
              ))}
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Шаг 3: мастера и графики. Новый мастер получает все услуги, график пн–сб 09–18 и место в конце списка.
 * Если владелец стрижёт сам, он добавляет и себя.
 */
export function MastersStep({ salonId, next, back }: StepProps) {
  const { t } = useTranslation();
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId }));
  const services = useQuery(trpc.calendar.services.list.queryOptions({ salonId }));
  const [name, setName] = useState("");
  const [freshId, setFreshId] = useState<string | null>(null);
  const addMaster = useMutation(trpc.calendar.masters.save.mutationOptions());
  const setHours = useMutation(trpc.calendar.workingHours.set.mutationOptions());
  const [adding, setAdding] = useState(false);
  const [failed, setFailed] = useState(false);
  useUnsavedChanges(name.trim() !== "");

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    setFailed(false);
    try {
      const m = await addMaster.mutateAsync({ salonId, name: name.trim(), serviceIds: (services.data ?? []).map((s) => s.id) });
      await setHours.mutateAsync({ masterId: m.id, hours: DEFAULT_HOURS });
      setName("");
      setFreshId(m.id);
      await queryClient.invalidateQueries(trpc.calendar.pathFilter());
    } catch {
      setFailed(true);
    } finally {
      setAdding(false);
    }
  };

  if (masters.isPending || services.isPending) return <Spinner />;
  const list = masters.data ?? [];
  const ready = list.length > 0 && list.every((m) => m.serviceIds.length > 0);
  return (
    <div>
      <p className="mb-4 text-ink-2">{t("onb.masters.intro")}</p>
      <div className="space-y-3">
        {list.map((m) => (
          <MasterCard key={m.id} salonId={salonId} master={m} services={services.data ?? []} justAdded={m.id === freshId} />
        ))}
      </div>
      <form onSubmit={(e) => void onAdd(e)} className="mt-4 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("onb.masters.namePlaceholder")} aria-label={t("onb.masters.name")} />
        <Button type="submit" disabled={adding || !name.trim()}>
          {t("onb.masters.add")}
        </Button>
      </form>
      {failed && <Notice kind="error">{t("common.error")}</Notice>}
      {!ready && <p className="mt-3 text-sm text-ink-3">{t("onb.masters.needOne")}</p>}
      <StepNav onBack={back} onNext={next} disabled={!ready} />
    </div>
  );
}
